import { LegalLayout, LegalSection } from "@/components/LegalLayout";
import { CONTACT_EMAIL } from "@/lib/assets";

export default function Terms() {
  return (
    <LegalLayout title="Terms of Service" updated="June 1, 2026" testId="terms-page">
      <p className="mt-8 text-[15px] leading-relaxed text-[var(--ink-soft)]">
        These Terms of Service ("Terms") govern your use of the Domaya mobile
        application and the website domaya.app (together, the "Service"),
        operated by Domaya ("we", "us" or "our"). By creating an account or
        using the Service, you agree to these Terms. If you do not agree, do
        not use the Service.
      </p>

      <LegalSection title="1. The Service">
        <p>
          Domaya is a household management app that helps you organise pantry
          items, shopping lists, routines, care notes, home maintenance tasks,
          reminders and household members, including optional AI-powered
          features such as pantry scanning and suggestions.
        </p>
      </LegalSection>

      <LegalSection title="2. Eligibility and Accounts">
        <p>
          You must be at least 13 years old (or the minimum age required in
          your jurisdiction) to use the Service. You are responsible for the
          accuracy of your account information, for keeping your credentials
          secure, and for all activity under your account. Notify us promptly
          at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--olive)] underline">
            {CONTACT_EMAIL}
          </a>{" "}
          if you suspect unauthorised use.
        </p>
      </LegalSection>

      <LegalSection title="3. Subscriptions and Payments">
        <p>
          Some features require a paid subscription. Subscriptions may be
          billed through Stripe, the Apple App Store or Google Play, depending
          on how you purchase. Prices and features are shown at the point of
          purchase.
        </p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>
            Subscriptions renew automatically until cancelled. You can cancel
            at any time; cancellation takes effect at the end of the current
            billing period.
          </li>
          <li>
            Purchases made through the App Store or Google Play are managed and
            refunded according to the respective store's policies.
          </li>
          <li>
            Except where required by law or by store policy, payments are
            non-refundable.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="4. Your Content">
        <p>
          You retain ownership of the content you add to Domaya (photos,
          pantry items, notes, tasks and other household data). You grant us a
          limited licence to host, process and display that content solely to
          operate and improve the Service for you and the household members
          you invite. You are responsible for the content you add and for
          ensuring you have the right to share it with your household.
        </p>
      </LegalSection>

      <LegalSection title="5. Acceptable Use">
        <p>You agree not to:</p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>use the Service for any unlawful purpose;</li>
          <li>upload content that is harmful, infringing or offensive;</li>
          <li>attempt to access other users' data or our systems without authorisation;</li>
          <li>reverse engineer, scrape or disrupt the Service; or</li>
          <li>resell or sublicense the Service without our written consent.</li>
        </ul>
      </LegalSection>

      <LegalSection title="6. AI Features">
        <p>
          AI-powered features (such as pantry scanning and recipe or task
          suggestions) generate results automatically and may be inaccurate or
          incomplete. They are provided for convenience only and do not
          constitute professional, medical, nutritional or safety advice.
          Always verify important information yourself — especially food
          expiry, allergens and safety-related tasks.
        </p>
      </LegalSection>

      <LegalSection title="7. Third-Party Services">
        <p>
          The Service integrates with third parties such as Google (sign-in
          and calendar), Stripe (payments) and Resend (email). Your use of
          those services is also governed by their own terms and privacy
          policies. We are not responsible for third-party services.
        </p>
      </LegalSection>

      <LegalSection title="8. Intellectual Property">
        <p>
          The Service, including its design, branding, software and content
          (excluding your content), is owned by Domaya and protected by
          intellectual property laws. We grant you a personal, non-exclusive,
          non-transferable licence to use the app on your devices for your
          personal, non-commercial use.
        </p>
      </LegalSection>

      <LegalSection title="9. Termination">
        <p>
          You may stop using the Service and delete your account at any time.
          We may suspend or terminate your access if you breach these Terms or
          if we reasonably believe your use poses a risk to the Service or
          other users. Upon termination, your right to use the Service ends
          and your data will be handled as described in our Privacy Policy.
        </p>
      </LegalSection>

      <LegalSection title="10. Disclaimers">
        <p>
          The Service is provided "as is" and "as available" without
          warranties of any kind, whether express or implied, including
          fitness for a particular purpose, accuracy and non-infringement. We
          do not warrant that the Service will be uninterrupted, error-free or
          secure.
        </p>
      </LegalSection>

      <LegalSection title="11. Limitation of Liability">
        <p>
          To the maximum extent permitted by law, Domaya will not be liable
          for any indirect, incidental, special, consequential or punitive
          damages, or any loss of data, profits or goodwill, arising from your
          use of the Service. Our total liability for any claim will not
          exceed the amount you paid us in the 12 months before the claim
          arose. Nothing in these Terms limits rights you have under
          applicable consumer protection law that cannot be excluded.
        </p>
      </LegalSection>

      <LegalSection title="12. Changes to These Terms">
        <p>
          We may update these Terms from time to time. If we make material
          changes we will notify you through the app or by email before they
          take effect. Continuing to use the Service after changes take effect
          means you accept the updated Terms.
        </p>
      </LegalSection>

      <LegalSection title="13. Governing Law">
        <p>
          These Terms are governed by the laws of Australia, without regard to
          conflict-of-law principles, and disputes will be subject to the
          non-exclusive jurisdiction of the Australian courts.
        </p>
      </LegalSection>

      <LegalSection title="14. Contact Us">
        <p>
          Questions about these Terms? Contact us at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--olive)] underline" data-testid="terms-contact-email">
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
